import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import { OutlinedButton } from 'components/OutlinedButton';
import TextField from 'components/text-field';
import { TextButton } from 'components/TextButton';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import LostPassword from './LostPassword';

const schema = z.object({
  email: z.string().min(1, 'required').email('Invalid email'),
});

type EmailForm = z.infer<typeof schema>;
type PendingAction = 'oauth' | 'link' | null;
type LoginStep = 'options' | 'email' | 'sent';

const useStyles = makeStyles((theme: any) =>
  createStyles({
    card: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: theme.spacing(48),
      margin: '0 auto',
      padding: theme.spacing(4),
      backgroundColor: theme.palette.dualBackground.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[8],
      '& .MuiButton-text:hover:not(.Mui-disabled) .MuiTypography-root': {
        color: theme.palette.button.hover,
      },
      '& .MuiButton-contained.Mui-disabled': {
        backgroundColor: theme.palette.primary.light,
      },
      '& .MuiButton-outlined.Mui-disabled': {
        borderColor: theme.palette.primary.light,
      },
      '& .MuiButton-root.Mui-disabled .MuiTypography-root': {
        color: theme.palette.grey[400],
      },
    },
    // dualText.secondary (27% white) measures ~2.4:1 here; grey[300]/grey[400] keep WCAG AA on the dark card.
    field: {
      '& .MuiFilledInput-root, & .MuiFilledInput-root:hover, & .MuiFilledInput-root.Mui-focused': {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.dualText.primary,
      },
      '& .MuiFilledInput-root.Mui-disabled': {
        color: theme.palette.grey[300],
      },
      '& .MuiFilledInput-underline:before': {
        borderBottomColor: theme.palette.grey[300],
      },
      '& .MuiFilledInput-underline:after': {
        borderBottomColor: theme.palette.secondary.main,
      },
      '& .MuiFilledInput-underline.Mui-error:after': {
        borderBottomColor: theme.palette.error.light,
      },
      '& .MuiInputLabel-root': {
        color: theme.palette.grey[300],
      },
      '& .MuiInputLabel-root.Mui-focused': {
        color: theme.palette.secondary.main,
      },
      '& .MuiInputLabel-root.Mui-error, & .MuiFormHelperText-root.Mui-error': {
        color: theme.palette.error.light,
      },
      '& .MuiIconButton-root': {
        color: theme.palette.grey[300],
      },
      '& .MuiFilledInput-input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px ${theme.palette.primary.light} inset`,
        WebkitTextFillColor: theme.palette.dualText.primary,
        caretColor: theme.palette.dualText.primary,
      },
    },
    stack: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      color: theme.palette.grey[400],
      '&::before, &::after': {
        content: '""',
        flex: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
      },
    },
    title: {
      color: theme.palette.dualText.primary,
    },
    muted: {
      color: theme.palette.grey[400],
    },
    oauthError: {
      color: theme.palette.error.light,
    },
    sent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(1),
      textAlign: 'center',
    },
    sentIcon: {
      color: theme.palette.success.main,
      marginBottom: theme.spacing(1),
    },
  })
);

type LoginFormProps = {
  handleOAuthLogin: () => Promise<void>;
  handleSendEmailLink: (values: EmailForm) => Promise<boolean>;
  oauthError: boolean;
  loginScene: 'login' | 'lost';
  handleChangeScene: (scene: 'login' | 'lost') => void;
  initialValues?: { email: string; secret: string };
};

const LoginForm = ({
  handleOAuthLogin,
  handleSendEmailLink,
  oauthError,
  loginScene,
  handleChangeScene,
  initialValues,
}: LoginFormProps) => {
  const classes = useStyles();
  const theme: any = useTheme();
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingAction>(null);
  const [step, setStep] = useState<LoginStep>('options');
  const [sentEmail, setSentEmail] = useState('');
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailForm>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });
  const isBusy = pending !== null;
  const linkColor = theme.palette.secondary.main;

  const onOAuth = async () => {
    setPending('oauth');
    try {
      await handleOAuthLogin();
    } finally {
      // On success the browser is already navigating to the authorize URL.
      setPending(null);
    }
  };

  const onSendLink = async (values: EmailForm) => {
    setPending('link');
    try {
      const sent = await handleSendEmailLink(values);
      if (sent) {
        setSentEmail(values.email);
        setStep('sent');
      }
    } finally {
      setPending(null);
    }
  };

  const backToOptions = () => {
    reset({ email: '' });
    setStep('options');
  };

  const renderOptions = () => (
    <>
      <Box className={classes.stack}>
        <ContainedButton
          text={t('auth:sign-in-with-oauth')}
          ariaLabel={t('auth:sign-in-with-oauth')}
          width="100%"
          onClick={onOAuth}
          isLoading={pending === 'oauth'}
          disabled={isBusy}
        />
        {oauthError && (
          <Typography variant="body2" role="alert" className={classes.oauthError}>
            {t('auth:oauth-error')}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" className={classes.divider}>
        {t('auth:or')}
      </Typography>
      <OutlinedButton
        text={t('auth:send-link')}
        icon="envelope"
        width="100%"
        textColor={theme.palette.dualText.primary}
        onClick={() => setStep('email')}
        disabled={isBusy}
      />
      <TextButton
        text={t('auth:lost-password')}
        buttonColor="secondary"
        textColor={linkColor}
        customStyle={{ alignSelf: 'center' }}
        disabled={isBusy}
        onClick={() => handleChangeScene('lost')}
      />
    </>
  );

  const renderEmailStep = () => (
    <form onSubmit={handleSubmit(onSendLink)} className={classes.stack} noValidate>
      <Typography variant="h6" component="h2" className={classes.title}>
        {t('auth:email-link-title')}
      </Typography>
      <Typography variant="body2" className={classes.muted}>
        {t('auth:email-link-description')}
      </Typography>
      <Controller
        name="email"
        control={control}
        render={({ field: { ref, ...field } }) => (
          <TextField
            customClassName={classes.field}
            {...field}
            inputRef={ref}
            inputProps={{ autoFocus: true, type: 'email', autoComplete: 'email' }}
            label={t('auth:emailLabel')}
            disabled={isBusy}
            error={!!errors.email}
            helperText={errors.email?.message}
          />
        )}
      />
      <ContainedButton
        type="submit"
        text={t('auth:send-link')}
        ariaLabel={t('auth:send-link')}
        width="100%"
        isLoading={pending === 'link'}
        disabled={isBusy}
      />
      <TextButton
        text={t('auth:back')}
        icon="arrow-left"
        buttonColor="secondary"
        textColor={linkColor}
        customStyle={{ alignSelf: 'flex-start' }}
        disabled={isBusy}
        onClick={() => setStep('options')}
      />
    </form>
  );

  const renderSent = () => (
    <Box className={classes.sent} role="status">
      <FontAwesomeIcon icon="check-circle" size="2x" className={classes.sentIcon} />
      <Typography variant="h6" component="h2" className={classes.title}>
        {t('auth:email-sent-success')}
      </Typography>
      <Typography variant="body2" className={classes.muted}>
        {t('auth:email-sent-detail', { email: sentEmail })}
      </Typography>
      <TextButton
        text={t('auth:back-to-sign-in')}
        buttonColor="secondary"
        textColor={linkColor}
        onClick={backToOptions}
      />
    </Box>
  );

  const renderLoginStep = () => {
    switch (step) {
      case 'options':
        return renderOptions();
      case 'email':
        return renderEmailStep();
      case 'sent':
        return renderSent();
      default: {
        const unreachable: never = step;
        return unreachable;
      }
    }
  };

  return (
    <Paper className={classes.card}>
      {loginScene === 'login' && renderLoginStep()}
      {loginScene === 'lost' && (
        <LostPassword
          handleReset={() => handleChangeScene('login')}
          initialValues={initialValues}
          fieldClassName={classes.field}
        />
      )}
    </Paper>
  );
};

export default LoginForm;
